import * as React from "react"
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip"

import { cn } from "@/lib/utils"

function TooltipProvider({
  delayDuration = 300,
  ...props
}: {
  delayDuration?: number
} & Omit<React.ComponentProps<typeof TooltipPrimitive.Provider>, "delay">) {
  return <TooltipPrimitive.Provider delay={delayDuration} {...props} />
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

type TooltipTriggerProps = React.ComponentProps<typeof TooltipPrimitive.Trigger> & {
  asChild?: boolean
}

function TooltipTrigger({ asChild, children, ...props }: TooltipTriggerProps) {
  if (!asChild) {
    return (
      <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props}>
        {children}
      </TooltipPrimitive.Trigger>
    )
  }
  const child = React.Children.only(children) as React.ReactElement
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" render={child} {...props} />
}

function TooltipContent({
  className,
  side,
  sideOffset = 4,
  align,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Popup> & {
  side?: "top" | "bottom" | "left" | "right"
  sideOffset?: number
  align?: React.ComponentProps<typeof TooltipPrimitive.Positioner>["align"]
}) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner side={side} sideOffset={sideOffset} align={align} className="z-50">
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            "origin-[var(--transform-origin)] overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground transition-[opacity,transform] duration-150 data-starting-style:opacity-0 data-starting-style:scale-95 data-ending-style:opacity-0 data-ending-style:scale-95",
            className,
          )}
          {...props}
        />
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
